/**
 * useFeatureStoresSubscription — abonne le shell aux stores Zustand déclarés
 * par chaque feature dans son `manifest.watchStores`.
 *
 * @remarks
 * Permet au shell de **re-render** quand un store de feature change, **sans
 * importer le store directement**. C'est l'inversion de dépendance qui fixe
 * l'anti-pattern principal du legacy (l'import `useTechTestsStore` dans
 * `MainLayout.tsx`).
 *
 * **Pourquoi `getAllFeatures` et pas `getActiveFeatures`** : la liste des
 * features (et donc le nombre de hooks appelés dans la boucle) **doit être
 * stable entre renders** — c'est la règle des hooks de React. Filtrer par
 * contexte casserait cet invariant à chaque navigation (un manifest avec
 * `watchStores` actif sur `/tests` mais inactif sur `/settings` ferait
 * passer le nombre de hooks de N à 0 → React explose au render suivant).
 *
 * Trade-off accepté : on s'abonne à tous les `watchStores` du registry,
 * pas seulement les pertinents au contexte courant. Le coût est négligeable
 * — les selectors renvoient des primitives stables, donc pas de re-render
 * inutile.
 *
 * **Pourquoi un selector obligatoire** : sans, on s'abonne à TOUT le store
 * → re-render à chaque update (catastrophique sur stores fréquemment mis
 * à jour). Le selector renvoie une primitive ou clé stable → le shell ne
 * re-render que quand cette valeur change.
 */

import { featureRegistry } from '@/app/registry';

export function useFeatureStoresSubscription(): void {
  // ⚠️ `getAllFeatures()` est figé après `markInitialized()` → nombre de
  // hooks stable entre renders. Ne PAS utiliser `getActiveFeatures(context)`
  // ici, ça violerait la règle des hooks à chaque changement de route.
  const features = featureRegistry.getAllFeatures();

  for (const manifest of features) {
    const watchEntries = manifest.watchStores;
    if (!watchEntries) continue;

    for (const entry of watchEntries) {
      // Chaque appel `useStore(selector)` est un hook React.
      entry.store(entry.selector);
    }
  }
}
